import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { getDeletedProjectsV2, restoreProjectV2, type DeletedProjectV2Dto } from '@/lib/api/v2/projects'
import { formatDate } from '@/lib/format'
import { showErrorToast, showSuccessToast } from '@/lib/toast'

/**
 * SOLY-ORDER-002 MS-04, renamed by fiche 17 — collapsible « Corbeille » view. Self-contained:
 * loads deleted projects, restores one, removes it from the list on success. Fermée par défaut,
 * masquée entièrement quand vide. A deleted project is in a trash bin: you restore it, you don't
 * browse it (the global DeletedAt == null filter 404s its detail), so the card is not navigable.
 *
 * Restoring brings the dossier back but NOT its sale (fiche 17 AC-4) — the copy says so, because a
 * silent half-restore is exactly the kind of thing a user discovers three weeks later.
 */
export function DeletedProjectsSection() {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<DeletedProjectV2Dto[]>([])
  const [restoringId, setRestoringId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getDeletedProjectsV2()
      .then((rows) => {
        if (!cancelled) setItems(rows)
      })
      .catch(() => {
        // Silent: an empty Corbeille is the correct fallback, no need to alarm the user.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleRestore = async (id: string) => {
    try {
      setRestoringId(id)
      await restoreProjectV2(id)
      setItems((prev) => prev.filter((p) => p.id !== id))
      showSuccessToast('Projet restauré')
    } catch {
      showErrorToast('La restauration a échoué, réessayez')
    } finally {
      setRestoringId(null)
    }
  }

  if (items.length === 0) return null

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-3" data-testid="deleted-projects-section">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-2 text-left text-sm hover:bg-muted/40"
          data-testid="deleted-projects-toggle"
        >
          <div className="flex items-center gap-2 text-muted-foreground">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="font-medium">Corbeille</span>
          </div>
          <span className="text-xs font-medium text-muted-foreground">{items.length}</span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <div
            key={p.id}
            className="rounded-lg border border-dashed bg-muted/30 p-4 text-muted-foreground"
            data-testid="deleted-project-card"
          >
            <div className="min-w-0">
              <div className="font-medium truncate text-foreground/70">{p.name || p.reference || '—'}</div>
              {p.clientName && <div className="truncate text-xs">{p.clientName}</div>}
            </div>

            <div className="mt-2 text-xs">Supprimé le {p.deletedAt ? formatDate(p.deletedAt) : '—'}</div>

            <div className="mt-3 flex items-center justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs"
                disabled={restoringId === p.id}
                onClick={() => handleRestore(p.id)}
                data-testid="project-restore-btn"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {restoringId === p.id ? 'Restauration…' : 'Restaurer'}
              </Button>
            </div>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}
